import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { EmptyState, ErrorState, SkeletonRows } from '@/components/admin/states';
import { Notice } from '@/components/admin/Notice';
import { fetchCohorts, fetchCourses } from '@/features/masters/api';
import { fetchMembers } from '@/features/members/api';

/** 一度に読む件数。期・コースで絞ったあとの人数はこれを超えない想定（超えたら画面で知らせる） */
const PER_PAGE = 100;

/**
 * 面談（サクセス面談・1on1）の対象になる受講生を1人選ぶ（D-176）。
 *
 * 受講生が130名規模になると全員を並べたプルダウンでは選べないため、
 * **先に期とコースで絞ってから**、在籍中の受講生をラジオボタンで選ぶ形にする。
 * コースは任意（未選択ならその期の全コース）。期とコースはセットで扱う（D-114）。
 */
export function MemberPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (memberId: string) => void;
}): ReactElement {
  const [cohortId, setCohortId] = useState('');
  const [courseId, setCourseId] = useState('');

  const cohorts = useQuery({ queryKey: ['cohorts'], queryFn: fetchCohorts });
  const courses = useQuery({ queryKey: ['courses'], queryFn: fetchCourses });
  const members = useQuery({
    queryKey: ['member-picker', cohortId, courseId],
    queryFn: () =>
      fetchMembers({
        cohort_id: cohortId,
        course_id: courseId || null,
        status: 'active',
        per_page: PER_PAGE,
      }),
    enabled: cohortId !== '',
  });

  /** 絞り込みを変えたら選択は解除する（画面から消えた人が選ばれたままにならないように） */
  function changeFilter(next: { cohortId?: string; courseId?: string }): void {
    if (next.cohortId !== undefined) setCohortId(next.cohortId);
    if (next.courseId !== undefined) setCourseId(next.courseId);
    if (value) onChange('');
  }

  const items = members.data?.items ?? [];
  const total = members.data?.total ?? 0;

  return (
    <div className="rounded-sm border border-wpborder p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">
            期<span className="text-wpnotice-error"> *</span>
          </span>
          <select
            value={cohortId}
            onChange={(e) => changeFilter({ cohortId: e.target.value })}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="">選択してください</option>
            {(cohorts.data ?? [])
              .filter((c) => c.is_active || c.id === cohortId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">コース</span>
          <select
            value={courseId}
            onChange={(e) => changeFilter({ courseId: e.target.value })}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="">すべて</option>
            {(courses.data ?? [])
              .filter((c) => c.is_active || c.id === courseId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
      </div>

      <div className="mt-3">
        {cohortId === '' ? (
          <p className="px-1 py-3 text-wptext-muted">
            期を選ぶと、その期に<strong>在籍中</strong>の受講生が一覧で出ます。
            コースでさらに絞り込めます。
          </p>
        ) : members.isPending ? (
          <SkeletonRows rows={4} columns={2} />
        ) : members.isError ? (
          <ErrorState message="受講生を読み込めませんでした。" onRetry={() => void members.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            title="該当する受講生がいません"
            description="この期・コースに在籍中の受講生が登録されていません。期・コースを変えるか、会員の登録内容をご確認ください。"
          />
        ) : (
          <>
            <p className="mb-1 text-wptext-muted">
              在籍中の受講生 {total}名{courseId ? '（コース絞り込みあり）' : ''}
            </p>
            {total > items.length && (
              <Notice kind="warning" dismissible={false}>
                人数が多いため、先頭の{items.length}名だけを表示しています。
                コースで絞り込んでください。
              </Notice>
            )}
            <div className="max-h-72 overflow-y-auto rounded-sm border border-wpborder">
              {items.map((m) => (
                <label
                  key={m.id}
                  className="flex cursor-pointer items-center gap-2 border-b border-wpborder px-3 py-2 last:border-b-0 hover:bg-wpcanvas"
                >
                  <input
                    type="radio"
                    name="member-picker"
                    checked={value === m.id}
                    onChange={() => onChange(m.id)}
                  />
                  <span className="font-medium">{m.name}</span>
                  <span className="text-wptext-muted">{m.member_id}</span>
                  {m.company && <span className="text-wptext-muted">／{m.company}</span>}
                </label>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
